import { formatNumber } from "accounting"

import { formatCurrency } from "../../../Utils/utils"

// The five headline tabs above the trend chart, read off the kpis/ answer. A tab's `key` is the
// trend endpoint's `metric`, so selecting a tab is what the chart asks for. `comparison` is the
// same figure over the comparison window, present only while a comparison is on.
export const buildKpiTabs = (kpis, t) => {
  const summary = kpis?.summary
  if (!summary) return []

  const compared = kpis?.comparison_summary
  const money = (v) => formatCurrency(v ?? 0, true)
  const avgRecovery = summary.won_count ? summary.recovered_amount / summary.won_count : 0

  return [
    // Every dispute in the window leads the row: the denominator the other four numbers are
    // shares of. How many are still in progress rides on its second line, so that number is
    // still on the page without a card of its own.
    {
      key: "disputes",
      label: t("All disputes"),
      value: summary.disputes_count,
      comparison: compared?.disputes_count,
      tooltip:
        "Every dispute we are handling for you in this period, whatever its status. Disputes decided before Recover was active are not counted here: you will find them in the disputes list, flagged Before Recover.",
      secondary: t("_count_ still in progress", { count: formatNumber(summary.active_count) })
    },
    {
      key: "recovered",
      label: t("Revenue recovered"),
      value: summary.recovered_amount,
      comparison: compared?.recovered_amount,
      prefix: "$",
      tooltip:
        "Amount won back on the disputes we fought for you. Wins that were already decided before you connected are not counted here.",
      secondary: t("_amount_ average recovery", { amount: money(avgRecovery) }),
      secondaryPrivate: true
    },
    {
      key: "at_risk",
      label: t("Revenue at risk"),
      value: summary.at_risk_amount,
      comparison: compared?.at_risk_amount,
      prefix: "$",
      tooltip: "Total value of disputes still open and being fought.",
      secondary: t("across _count_ open disputes", { count: formatNumber(summary.active_count) })
    },
    // Win rate only exists once an issuer has ruled on something. Until then the honest
    // value is "no answer yet", and 0% (or a divide-by-zero dash) reads as a failure
    // that has not happened, so the card is left out entirely and the rest of the row
    // stretches to fill it. It reappears on its own with the first decision.
    ...(summary.resolved_count > 0
      ? [
          {
            key: "win_rate",
            label: t("Win rate"),
            value: summary.win_rate,
            comparison: compared?.win_rate,
            suffix: "%",
            tooltip:
              "Of the disputes we fought to a decision, the share we won. Issuers can take up to 90 days to decide after a response is sent, so a dispute stays out of this figure until they rule. If one has been waiting longer than that, contact support and we will chase it.",
            secondary: t("_won_ won of _resolved_ resolved", {
              won: formatNumber(summary.won_count),
              resolved: formatNumber(summary.resolved_count)
            })
          }
        ]
      : []),
    {
      key: "avg_value",
      label: t("Average dispute value"),
      value: summary.avg_amount,
      comparison: compared?.avg_amount,
      prefix: "$",
      tooltip: "Average dollar amount across all disputes.",
      secondary: t("across _count_ disputes", { count: formatNumber(summary.disputes_count) })
    }
  ]
}
